<script>
import LogoComponent from '../Logo';
import { iIcon } from '@inscribe/signal';
import palette from '@inscribe/signal/dist/palette';
import UserArea from './UserArea';
import { mapGetters } from 'vuex';

export default {
  components: { iIcon, LogoComponent, UserArea },
  props: {
    collapsed: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    palette,
    hoveredItem: null,
    selectedItem: null,
  }),
  computed: {
    ...mapGetters({
      collectEnabled: 'collectEnabled',
    }),
    currentRoutePath() {
      return this.$route.fullPath;
    },
    menuItems() {
      const items = [
        {
          id: 'customers',
          icon: 'medium-user-multiple',
          label: 'Customers',
          detectRoutePaths: ['/customers', '/customer'],
          navigationRoutePath: '/customers',
        },
        {
          id: 'documents',
          icon: 'medium-file',
          label: 'Documents',
          detectRoutePaths: ['/documents'],
          navigationRoutePath: '/documents',
        },
        {
          id: 'decision-engine',
          icon: 'medium-network-cluster',
          label: 'Decision Engine',
          detectRoutePaths: ['/decision-engine'],
          navigationRoutePath: '/decision-engine',
        },
        {
          id: 'analytics',
          icon: 'medium-money-graph-bar',
          label: 'Analytics',
          detectRoutePaths: ['/analytics'],
          navigationRoutePath: '/analytics',
        },
        {
          id: 'settings',
          icon: 'medium-setting-cog',
          label: 'Settings',
          detectRoutePaths: ['/settings'],
          navigationRoutePath: '/settings',
        },
      ];
      if (this.collectEnabled) {
        items.splice(3, 0, {
          id: 'collect',
          icon: 'medium-file-request',
          label: 'Collect',
          detectRoutePaths: ['/collect'],
          navigationRoutePath: '/collect',
        });
      }
      return items;
    },
  },
  watch: {
    // watchers to detect when the collectEnabled flags change,
    // as they will initially be undefined and we need to detect the selected item
    collectEnabled(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.detectSelectedItem();
      }
    },
    $route() {
      this.detectSelectedItem();
    },
  },
  mounted() {
    this.detectSelectedItem();
  },
  methods: {
    detectSelectedItem() {
      this.menuItems.forEach((item) => {
        item.detectRoutePaths.forEach((path) => {
          if (this.currentRoutePath?.includes(path)) {
            this.selectedItem = item;
          }
        });
      });
    },
    openCustomersPage() {
      this.onItemClick({
        navigationRoutePath: '/customers',
      });
    },
    onItemClick(item) {
      if (
        item.navigationRoutePath &&
        !this.currentRoutePath.startsWith(item.navigationRoutePath)
      ) {
        this.$router.push({
          path: item.navigationRoutePath,
        });
      }
    },
  },
};
</script>

<template>
  <div class="left-nav i-p-8 d-flex flex-column">
    <div
      class="logo-container i-my-8 i-pl-8 d-flex align-items-center"
      @click="openCustomersPage"
    >
      <logo-component :is-mobile="collapsed" :color="'#39075B'" height="21px" />
    </div>
    <div
      :class="{ 'align-items-center': collapsed }"
      class="menu-container d-flex flex-column justify-content-start"
    >
      <button
        v-for="item in menuItems"
        :key="item.label"
        :class="{
          collapsed,
          selected: selectedItem === item,
          'justify-content-center': collapsed,
        }"
        class="menu-item-btn d-flex align-items-center i-p-8"
        :data-testid="'left-nav-item-' + item.id"
        @click="() => onItemClick(item)"
        @mouseover="hoveredItem = item"
        @mouseleave="hoveredItem = null"
      >
        <div class="content d-flex flex-row align-items-center">
          <i-icon
            :symbol="item.icon"
            :size="20"
            :colour="
              selectedItem === item
                ? palette['light-primary-100']
                : hoveredItem === item
                ? palette['light-neutral-100']
                : palette['light-neutral-90']
            "
            :class="{ 'i-mr-6': !collapsed }"
            class="menu-icon"
          ></i-icon>
          <div class="label i-text-subheading-2 i-text-neutral-120">
            {{ item.label }}
          </div>
        </div>
      </button>
    </div>
    <user-area class="user-container" :collapsed="collapsed" />
  </div>
</template>

<style lang="scss" scoped>
.left-nav {
  background: $light-neutral-20;
  height: 100%;

  .logo-container {
    cursor: pointer;
  }

  .menu-container {
    flex: 1;
    gap: 2px;

    .menu-item-btn {
      background-color: transparent;
      height: 40px;
      border: 0;
      border-radius: 12px;
      outline: 0;
      transition: background-color 150ms ease-in-out;

      &:hover {
        background-color: $light-neutral-default;
      }

      &.selected {
        background-color: $light-neutral-default;
        box-shadow: 0px 0px 24px 0px rgba(0, 0, 0, 0.04);
      }

      &.collapsed {
        width: 40px;
        .label {
          display: none;
        }
      }
    }
  }
}
</style>
